import React, { useState, useMemo, useCallback, useRef, useEffect } from 'react';
import {
  Container,
  Typography,
  Box,
  Grid,
  Card,
  Button,
  TextField,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  IconButton,
  Fade,
  Checkbox,
  FormControlLabel,
} from '@mui/material';
import {
  ExpandMore,
  Add,
  Remove,
  Home,
  Weekend,
  Kitchen,
  Bed,
  Bathtub,
  Deck,
  Garage,
  Stairs,
  Grass,
  Work,
  Hotel,
  LocalShipping
} from '@mui/icons-material';

// Debounce hook
const useDebounce = (value, delay) => {
  const [debouncedValue, setDebouncedValue] = useState(value);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    return () => {
      clearTimeout(handler);
    };
  }, [value, delay]);

  return debouncedValue;
};

// Debounced TextField Component
const DebouncedTextField = React.memo(({ value, onChange, ...props }) => {
  const [localValue, setLocalValue] = useState(value);
  
  useEffect(() => {
    setLocalValue(value);
  }, [value]);

  const debouncedValue = useDebounce(localValue, 300);

  useEffect(() => {
    if (debouncedValue !== value) {
      onChange({ target: { value: debouncedValue } });
    }
  }, [debouncedValue, onChange, value]);

  const handleChange = (e) => {
    setLocalValue(e.target.value);
  };

  return <TextField {...props} value={localValue} onChange={handleChange} />;
});

const FurnitureCalculator = () => {
  const [expandedCategory, setExpandedCategory] = useState(false);
  const [items, setItems] = useState({});
  const [startAddress, setStartAddress] = useState({
    address: '',
    zusatz: '',
    etage: '',
    ort: '',
    region: '',
    plz: '',
    land: 'Deutschland',
    halteverbot: false,
    trageweg: 25,
    abbau: false
  });
  const [endAddress, setEndAddress] = useState({
    address: '',
    zusatz: '',
    etage: '',
    ort: '',
    region: '',
    plz: '',
    land: 'Deutschland',
    halteverbot: false,
    trageweg: 25,
    aufbau: false
  });
  const [contactInfo, setContactInfo] = useState({
    name: '',
    email: '',
    phone: '',
    date: new Date().toISOString().split('T')[0],
    comments: ''
  });

  const categories = useMemo(() => [
    {
      name: 'Wohnzimmer',
      icon: <Weekend />,
      items: [
        'Umzugskartons',
        'Stuhl',
        'Teppich',
        'Tisch',
        'Sessel',
        'Bücherregal',
        'Fernseher',
        'Sideboard',
        'Schreibtisch unter 160cm',
        'Schreibtisch über 160cm',
        'Buffet, mit Aufsatz',
        'Sofa, Couch',
        'Liege',
        'Sonstiges'
      ]
    },
    {
      name: 'Diele',
      icon: <Home />,
      items: [
        'Deckenlampe',
        'Kommode / Truhe',
        'Wäscheschrank / Wäschetruhe',
        'Garderobe / Hut-, Kleiderablage',
        'Schuhschrank',
        'Toilettenschrank',
        'Stuhl, Hocker',
        'Umzugskartons'
      ]
    },
    {
      name: 'Kinderzimmer',
      icon: <Bed />,
      items: [
        'Schrank, bis 2 Türen, nicht zerlegb.',
        'Bett, komplett',
        'Spielzeugkiste',
        'Schreibpult',
        'Umzugskartons',
        'Etagenbett, komplett',
        'Kinderbett, komplett',
        'Schrank _m, zerlegbar',
        'Schreibtisch',
        'Stühle',
        'Sonstiges'
      ]
    },
    {
      name: 'Schlafzimmer',
      icon: <Bed />,
      items: [
        'Futonbett',
        'Schrank - bis 2 Türen nicht zerlegbar',
        'Spiegel, über 0,8 m',
        'Kommode / mit Spiegel',
        'Stuhl, Hocker',
        'Nachttisch',
        'Fernseher Schlafzimmer',
        'Umzugskartons',
        'Schrank _m zerlegbar',
        'Boxspringbrett',
        'Bettgestell',
        'Sonstiges'
      ]
    },
    {
      name: 'Badezimmer',
      icon: <Bathtub />,
      items: [
        'Spiegelschrank',
        'Unterschrank',
        'Hängeschrank',
        'Sonstiges',
        'Umzugskartons',
        'Hochschrank',
        'Wäschekorb'
      ]
    },
    {
      name: 'Küche',
      icon: <Kitchen />,
      items: [
        'Schrank',
        'Küchenzeile',
        'Waschmaschine',
        'Herd',
        'Geschirrspüler',
        'Tisch',
        'Stühle',
        'Sonstiges',
        'Umzugskartons',
        'Kühlschrank'
      ]
    },
    {
      name: 'Dachboden',
      icon: <Home />,
      items: [
        'Regal',
        'Fahrrad Dachboden',
        'Sonstiges',
        'Umzugskartons',
        'Schrank _m, zerlegbar'
      ]
    },
    {
      name: 'Keller',
      icon: <Stairs />,
      items: [
        'Kinderwagen',
        'Koffer',
        'Schlitten',
        'Ski',
        'Werkbank, zerlegb.',
        'Werkzeugkoffer',
        'Werkzeugschrank',
        'Umzugskartons',
        'Sonstiges'
      ]
    },
    {
      name: 'Garage',
      icon: <Garage />,
      items: [
        'Autoreifen',
        'Fahrrad Garage',
        'Schubkarre',
        'Sonnenschirm',
        'Mülltonne',
        'Rasenmäher',
        'Dreirad / Kinderrad',
        'Blumenkübel / Kasten',
        'Umzugskartons',
        'Sonstiges'
      ]
    },
    {
      name: 'Flur',
      icon: <Home />,
      items: [
        'Umzugskartons',
        'Schauschrank',
        'Kommode',
        'Bank',
        'Schrank _m zerlegbar',
        'Garderobe _m zerlegbar',
        'Sonstiges'
      ]
    },
    {
      name: 'Terrasse',
      icon: <Deck />,
      items: [
        'Umzugskartons',
        'Stühle',
        'Sessel',
        'Sofa/Couch',
        'Tisch',
        'Sonstiges'
      ]
    },
    {
      name: 'Gartenhaus',
      icon: <Grass />,
      items: [
        'Umzugskartons',
        'Rasenmäher',
        'Schubkarre',
        'Sonstiges',
        'Gartengeräte'
      ]
    },
    {
      name: 'Arbeitszimmer',
      icon: <Work />,
      items: [
        'Umzugskartons',
        'Bett',
        'Sofa/Couch',
        'Schreibtisch',
        'Stühle',
        'Regal _m zerlegbar',
        'Schrank _m zerlegbar',
        'Sonstiges'
      ]
    },
    {
      name: 'Gästezimmer',
      icon: <Hotel />,
      items: [
        'Umzugskartons',
        'Bett',
        'Schrank _m, zerlegbar',
        'Sessel',
        'Regal _m, zerlegbar',
        'Sonstiges'
      ]
    }
  ], []);

  const handleCategoryChange = useCallback((panel) => (event, isExpanded) => {
    setExpandedCategory(isExpanded ? panel : false);
  }, []);

  const handleItemChange = useCallback((category, item, value) => {
    const key = `${category}-${item}`;
    setItems(prev => ({
      ...prev,
      [key]: Math.max(0, value)
    }));
  }, []);

  const getItemCount = useCallback((category, item) => {
    const key = `${category}-${item}`;
    return items[key] || 0;
  }, [items]);

  const calculateVolume = useMemo(() => {
    const volumeMap = {
      'Umzugskartons': 0.05,
      'Stuhl': 0.2,
      'Tisch': 1.5,
      'Sofa, Couch': 2.5,
      'Schrank': 2.0,
      'Bett': 2.0,
      'Kühlschrank': 1.5,
      'Waschmaschine': 0.8,
      'default': 0.5
    };

    let totalVolume = 0;
    Object.entries(items).forEach(([key, count]) => {
      const itemName = key.split('-')[1];
      let itemVolume = volumeMap.default;
      
      Object.keys(volumeMap).forEach(mapKey => {
        if (itemName.includes(mapKey)) {
          itemVolume = volumeMap[mapKey];
        }
      });
      
      totalVolume += itemVolume * count;
    });

    return totalVolume.toFixed(2);
  }, [items]);

  const handleSubmit = useCallback(async () => {
    const formData = {
      items,
      startAddress,
      endAddress,
      contactInfo,
      totalVolume: calculateVolume
    };
    
    // Get button and original text before try block
    const submitButton = document.querySelector('[data-submit-button]');
    const originalText = submitButton?.textContent || 'Jetzt Umzugsanfrage senden';
    
    try {
      // Show loading state
      if (submitButton) {
        submitButton.disabled = true;
        submitButton.textContent = 'Wird gesendet...';
      }

    
      const response = await fetch('https://api.hnumzugsservice.de/api/calculator/submit', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData)
      });

      const result = await response.json();

      if (response.ok && result.success) {
        alert(result.message || 'Ihre Anfrage wurde erfolgreich gesendet! Wir werden uns in Kürze bei Ihnen melden.');
        
        // Reset form
        setItems({});
        setStartAddress({
          address: '',
          zusatz: '',
          etage: '',
          ort: '',
          region: '',
          plz: '',
          land: 'Deutschland',
          halteverbot: false,
          trageweg: 25,
          abbau: false
        });
        setEndAddress({
          address: '',
          zusatz: '',
          etage: '',
          ort: '',
          region: '',
          plz: '',
          land: 'Deutschland',
          halteverbot: false,
          trageweg: 25,
          aufbau: false
        });
        setContactInfo({
          name: '',
          email: '',
          phone: '',
          date: new Date().toISOString().split('T')[0],
          comments: ''
        });
        setExpandedCategory(false);
      } else {
        throw new Error(result.message || 'Ein Fehler ist aufgetreten');
      }

    } catch (error) {
      console.error('Error submitting form:', error);
      alert(error.message || 'Ein Fehler ist aufgetreten. Bitte versuchen Sie es später erneut.');
    } finally {
      // Restore button state
      if (submitButton) {
        submitButton.disabled = false;
        submitButton.textContent = originalText;
      }
    }
  }, [items, startAddress, endAddress, contactInfo, calculateVolume]);

  // Memoized handlers for text fields to prevent re-renders
  const handleStartAddressChange = useCallback((field) => (e) => {
    const value = e.target.type === 'checkbox' ? e.target.checked : e.target.value;
    setStartAddress(prev => ({ ...prev, [field]: value }));
  }, []);

  const handleEndAddressChange = useCallback((field) => (e) => {
    const value = e.target.type === 'checkbox' ? e.target.checked : e.target.value;
    setEndAddress(prev => ({ ...prev, [field]: value }));
  }, []);

  const handleContactInfoChange = useCallback((field) => (e) => {
    setContactInfo(prev => ({ ...prev, [field]: e.target.value }));
  }, []);

  const textFieldStyle = useMemo(() => ({
    '& .MuiOutlinedInput-root': {
      color: 'white',
      '& fieldset': {
        borderColor: '#55d6dc'
      },
      '&:hover fieldset': {
        borderColor: '#55d6dc'
      },
      '&.Mui-focused fieldset': {
        borderColor: '#55d6dc'
      }
    },
    '& .MuiInputLabel-root': {
      color: '#55d6dc'
    },
    '& .MuiInputLabel-root.Mui-focused': {
      color: '#55d6dc'
    }
  }), []);

  const dateFieldStyle = useMemo(() => ({
    ...textFieldStyle,
    '& input[type="date"]': {
      fontSize: '16px',
      padding: '16.5px 14px',
      height: '56px',
      '-webkit-appearance': 'none',
      'appearance': 'none',
      backgroundColor: 'rgba(85, 214, 220, 0.1)',
      color: 'white',
      cursor: 'pointer',
      border: '1px solid #55d6dc',
      borderRadius: '4px',
      '&::-webkit-calendar-picker-indicator': {
        filter: 'invert(1)',
        cursor: 'pointer',
        fontSize: '20px',
        padding: '5px',
        opacity: 1
      },
      '&::-webkit-datetime-edit': {
        padding: '0',
        fontSize: '16px',
        color: 'white'
      },
      '&::-webkit-datetime-edit-fields-wrapper': {
        padding: '0',
        color: 'white'
      },
      '&::-webkit-datetime-edit-text': {
        padding: '0 4px',
        color: '#55d6dc'
      },
      '&::-webkit-datetime-edit-month-field': {
        textTransform: 'uppercase',
        color: 'white'
      },
      '&::-webkit-datetime-edit-day-field': {
        textTransform: 'uppercase',
        color: 'white'
      },
      '&::-webkit-datetime-edit-year-field': {
        textTransform: 'uppercase',
        color: 'white'
      },
      '&::-webkit-date-and-time-value': {
        color: 'white',
        textAlign: 'left'
      }
    },
    '@media (max-width: 768px)': {
      '& input[type="date"]': {
        fontSize: '18px',
        padding: '20px 14px',
        height: '60px'
      }
    }
  }), [textFieldStyle]);

  return (
    <Box sx={{ minHeight: '100vh', pt: 10, pb: 10 }}>
      <Container maxWidth="lg">
        <Fade in timeout={1000}>
          <Box>

            {/* Furniture Items Section */}
            <Box sx={{ mb: 6 }}>
              <Typography variant="h4" sx={{ mb: 3, color: 'white', fontWeight: 'bold' }}>
                1. Möbel und Gegenstände
              </Typography>
              {categories.map((category, index) => (
                <Accordion
                  key={index}
                  expanded={expandedCategory === category.name}
                  onChange={handleCategoryChange(category.name)}
                  sx={{
                    mb: 2,
                    bgcolor: '#1a3a44',
                    color: 'white',
                    '&:before': {
                      display: 'none'
                    }
                  }}
                >
                  <AccordionSummary
                    expandIcon={<ExpandMore sx={{ color: '#55d6dc' }} />}
                    sx={{
                      '&:hover': {
                        bgcolor: '#0f2832'
                      }
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                      <Box sx={{ color: '#55d6dc' }}>{category.icon}</Box>
                      <Typography variant="h6">{category.name}</Typography>
                    </Box>
                  </AccordionSummary>
                  <AccordionDetails>
                    <Grid container spacing={2}>
                      {category.items.map((item, itemIndex) => (
                        <Grid item xs={12} sm={6} md={4} key={itemIndex}>
                          <Box
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'space-between',
                              bgcolor: '#275664',
                              p: 2,
                              borderRadius: 1,
                              border: '1px solid transparent',
                              '&:hover': {
                                borderColor: '#55d6dc'
                              }
                            }}
                          >
                            <Typography variant="body2" sx={{ flex: 1 }}>
                              {item}
                            </Typography>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <IconButton
                                size="small"
                                onClick={() => handleItemChange(category.name, item, getItemCount(category.name, item) - 1)}
                                sx={{ color: '#55d6dc' }}
                              >
                                <Remove />
                              </IconButton>
                              <Typography sx={{ minWidth: '30px', textAlign: 'center' }}>
                                {getItemCount(category.name, item)}
                              </Typography>
                              <IconButton
                                size="small"
                                onClick={() => handleItemChange(category.name, item, getItemCount(category.name, item) + 1)}
                                sx={{ color: '#55d6dc' }}
                              >
                                <Add />
                              </IconButton>
                            </Box>
                          </Box>
                        </Grid>
                      ))}
                    </Grid>
                  </AccordionDetails>
                </Accordion>
              ))}
            </Box>

            {/* Start Address Section */}
            <Box sx={{ mb: 6 }}>
              <Typography variant="h4" sx={{ mb: 3, color: 'white', fontWeight: 'bold' }}>
                2. Startadresse
              </Typography>
              <Card sx={{ bgcolor: '#1a3a44', p: 3 }}>
                <Grid container spacing={3}>
                  <Grid item xs={12} md={6}>
                    <DebouncedTextField
                      fullWidth
                      label="Adresse"
                      value={startAddress.address}
                      onChange={handleStartAddressChange('address')}
                      sx={textFieldStyle}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <DebouncedTextField
                      fullWidth
                      label="Anschrift Zusatz"
                      value={startAddress.zusatz}
                      onChange={handleStartAddressChange('zusatz')}
                      sx={textFieldStyle}
                    />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <DebouncedTextField
                      fullWidth
                      label="Etage"
                      value={startAddress.etage}
                      onChange={handleStartAddressChange('etage')}
                      sx={textFieldStyle}
                    />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <DebouncedTextField
                      fullWidth
                      label="Ort"
                      value={startAddress.ort}
                      onChange={handleStartAddressChange('ort')}
                      sx={textFieldStyle}
                    />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <DebouncedTextField
                      fullWidth
                      label="PLZ"
                      value={startAddress.plz}
                      onChange={handleStartAddressChange('plz')}
                      sx={textFieldStyle}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <DebouncedTextField
                      fullWidth
                      label="Region"
                      value={startAddress.region}
                      onChange={handleStartAddressChange('region')}
                      sx={textFieldStyle}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <DebouncedTextField
                      fullWidth
                      label="Trageweg zur Haustür (Meter)"
                      type="number"
                      value={startAddress.trageweg}
                      onChange={handleStartAddressChange('trageweg')}
                      sx={textFieldStyle}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={startAddress.halteverbot}
                          onChange={handleStartAddressChange('halteverbot')}
                          sx={{ color: '#55d6dc', '&.Mui-checked': { color: '#55d6dc' } }}
                        />
                      }
                      label="Halteverbotszone an der Abholadresse"
                      sx={{ color: 'white' }}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={startAddress.abbau}
                          onChange={handleStartAddressChange('abbau')}
                          sx={{ color: '#55d6dc', '&.Mui-checked': { color: '#55d6dc' } }}
                        />
                      }
                      label="Abbau der Möbel gewünscht"
                      sx={{ color: 'white' }}
                    />
                  </Grid>
                </Grid>
              </Card>
            </Box>

            {/* End Address Section */}
            <Box sx={{ mb: 6 }}>
              <Typography variant="h4" sx={{ mb: 3, color: 'white', fontWeight: 'bold' }}>
                3. Zieladresse
              </Typography>
              <Card sx={{ bgcolor: '#1a3a44', p: 3 }}>
                <Grid container spacing={3}>
                  <Grid item xs={12} md={6}>
                    <DebouncedTextField
                      fullWidth
                      label="Adresse"
                      value={endAddress.address}
                      onChange={handleEndAddressChange('address')}
                      sx={textFieldStyle}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <DebouncedTextField
                      fullWidth
                      label="Anschrift Zusatz"
                      value={endAddress.zusatz}
                      onChange={handleEndAddressChange('zusatz')}
                      sx={textFieldStyle}
                    />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <DebouncedTextField
                      fullWidth
                      label="Etage"
                      value={endAddress.etage}
                      onChange={handleEndAddressChange('etage')}
                      sx={textFieldStyle}
                    />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <DebouncedTextField
                      fullWidth
                      label="Ort"
                      value={endAddress.ort}
                      onChange={handleEndAddressChange('ort')}
                      sx={textFieldStyle}
                    />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <DebouncedTextField
                      fullWidth
                      label="PLZ"
                      value={endAddress.plz}
                      onChange={handleEndAddressChange('plz')}
                      sx={textFieldStyle}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <DebouncedTextField
                      fullWidth
                      label="Region"
                      value={endAddress.region}
                      onChange={handleEndAddressChange('region')}
                      sx={textFieldStyle}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <DebouncedTextField
                      fullWidth
                      label="Trageweg zur Haustür (Meter)"
                      type="number"
                      value={endAddress.trageweg}
                      onChange={handleEndAddressChange('trageweg')}
                      sx={textFieldStyle}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={endAddress.halteverbot}
                          onChange={handleEndAddressChange('halteverbot')}
                          sx={{ color: '#55d6dc', '&.Mui-checked': { color: '#55d6dc' } }}
                        />
                      }
                      label="Halteverbotszone an der Zieladresse"
                      sx={{ color: 'white' }}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={endAddress.aufbau}
                          onChange={handleEndAddressChange('aufbau')}
                          sx={{ color: '#55d6dc', '&.Mui-checked': { color: '#55d6dc' } }}
                        />
                      }
                      label="Aufbau der Möbel gewünscht"
                      sx={{ color: 'white' }}
                    />
                  </Grid>
                </Grid>
              </Card>
            </Box>

            {/* Contact Information Section */}
            <Box sx={{ mb: 6 }}>
              <Typography variant="h4" sx={{ mb: 3, color: 'white', fontWeight: 'bold' }}>
                4. Kontaktinformationen
              </Typography>
              <Card sx={{ bgcolor: '#1a3a44', p: 3 }}>
                <Grid container spacing={3}>
                  <Grid item xs={12} md={6}>
                    <DebouncedTextField
                      fullWidth
                      label="Vor- /Nachname"
                      value={contactInfo.name}
                      onChange={handleContactInfoChange('name')}
                      sx={textFieldStyle}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <DebouncedTextField
                      fullWidth
                      label="E-Mail Adresse"
                      type="email"
                      value={contactInfo.email}
                      onChange={handleContactInfoChange('email')}
                      sx={textFieldStyle}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <DebouncedTextField
                      fullWidth
                      label="Telefon"
                      value={contactInfo.phone}
                      onChange={handleContactInfoChange('phone')}
                      sx={textFieldStyle}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Wunschdatum"
                      type="date"
                      value={contactInfo.date}
                      onChange={handleContactInfoChange('date')}
                      InputLabelProps={{
                        shrink: true,
                      }}
                      inputProps={{
                        min: new Date().toISOString().split('T')[0]
                      }}
                      sx={dateFieldStyle}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <DebouncedTextField
                      fullWidth
                      multiline
                      rows={4}
                      label="Kommentare oder Fragen"
                      value={contactInfo.comments}
                      onChange={handleContactInfoChange('comments')}
                      sx={textFieldStyle}
                    />
                  </Grid>
                </Grid>
              </Card>
            </Box>

            {/* Volume Display and Submit Button */}
            <Box sx={{ textAlign: 'center' }}>
              <Card sx={{ bgcolor: '#1a3a44', p: 3, mb: 4, display: 'inline-block' }}>
                <Typography variant="h4" sx={{ color: '#55d6dc', mb: 1 }}>
                  Geschätztes Volumen
                </Typography>
                <Typography variant="h2" sx={{ color: 'white', fontWeight: 'bold' }}>
                  {calculateVolume} m³
                </Typography>
              </Card>

              <Box>
                <Button
                  variant="contained"
                  size="large"
                  onClick={handleSubmit}
                  startIcon={<LocalShipping />}
                  data-submit-button
                  sx={{
                    bgcolor: '#55d6dc',
                    color: '#275664',
                    px: 6,
                    py: 2,
                    fontSize: '1.2rem',
                    fontWeight: 'bold',
                    '&:hover': {
                      bgcolor: '#4bc2c8'
                    }
                  }}
                >
                  Jetzt Umzugsanfrage senden
                </Button>
              </Box>
            </Box>
          </Box>
        </Fade>
      </Container>
    </Box>
  );
};

export default FurnitureCalculator;